import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  ACCESS_TYPE_OPTIONS,
  EMPTY_PACK_FORM,
  PACK_STATUS_FORM_OPTIONS,
  VISIBILITY_OPTIONS,
} from './stickerConstants';

export default function StickerPackEditModal({ open, packId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_PACK_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(packId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        if (packId) {
          const data = await adminApi.getImStickerPack(packId);
          if (cancelled) {
            return;
          }
          setForm({
            name: data.name || '',
            publisherDisplayName: data.publisherDisplayName || '',
            uploaderType: data.uploaderType || 'OFFICIAL',
            visibility: data.visibility || 'PUBLIC',
            accessType: data.accessType || 'FREE',
            subscriptionRequirement: data.subscriptionRequirement || '',
            status: data.status || 'ACTIVE',
            coverObjectKey: data.coverObjectKey || '',
            description: data.description || '',
            category: data.category || '',
            language: data.language || 'en_US',
            tags: data.tags || '',
            sort: data.sort ?? 0,
            version: data.version ?? 1,
          });
        } else {
          setForm({ ...EMPTY_PACK_FORM });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, packId]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCoverUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    setError('');
    try {
      const key = await adminApi.uploadOssImage(file);
      updateField('coverObjectKey', key);
    } catch (err) {
      setError(err.message || '封面上傳失敗');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.name?.trim() || !form.publisherDisplayName?.trim()) {
      setError('名稱與 Publisher 必填');
      return;
    }
    const payload = {
      ...form,
      name: form.name.trim(),
      publisherDisplayName: form.publisherDisplayName.trim(),
      subscriptionRequirement: form.subscriptionRequirement?.trim() || null,
      coverObjectKey: form.coverObjectKey || null,
      description: form.description?.trim() || null,
      category: form.category?.trim() || null,
      language: form.language?.trim() || null,
      tags: form.tags?.trim() || null,
      sort: Number(form.sort) || 0,
      version: Number(form.version) || 1,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateImStickerPack(packId, payload);
      } else {
        await adminApi.createImStickerPack(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '編輯貼圖包' : '新增貼圖包'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p>載入中…</p>
        ) : (
          <form className="form-grid" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <label>
              名稱
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </label>
            <label>
              Publisher
              <input
                type="text"
                value={form.publisherDisplayName}
                onChange={(e) => updateField('publisherDisplayName', e.target.value)}
                required
              />
            </label>
            <label>
              Access Type
              <select
                value={form.accessType}
                onChange={(e) => updateField('accessType', e.target.value)}
              >
                {ACCESS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Visibility
              <select
                value={form.visibility}
                onChange={(e) => updateField('visibility', e.target.value)}
              >
                {VISIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              訂閱要求
              <input
                type="text"
                value={form.subscriptionRequirement}
                onChange={(e) => updateField('subscriptionRequirement', e.target.value)}
                placeholder="如 VIP（FREE 可空）"
              />
            </label>
            <label>
              狀態
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                {PACK_STATUS_FORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              排序
              <input
                type="number"
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
              />
            </label>
            <label>
              版本
              <input
                type="number"
                value={form.version}
                onChange={(e) => updateField('version', e.target.value)}
              />
            </label>
            <label>
              分類
              <input
                type="text"
                value={form.category}
                onChange={(e) => updateField('category', e.target.value)}
              />
            </label>
            <label>
              標籤
              <input
                type="text"
                value={form.tags}
                onChange={(e) => updateField('tags', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              描述
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </label>
            <label className="form-grid__full">
              封面
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="file" accept="image/*" onChange={handleCoverUpload} disabled={uploading} />
                {form.coverObjectKey ? (
                  <img
                    src={resolveAdminMediaUrl(form.coverObjectKey)}
                    alt=""
                    style={{ width: 64, height: 64, objectFit: 'cover' }}
                  />
                ) : null}
              </div>
              <small>{form.coverObjectKey || '尚未上傳'}</small>
            </label>

            <div className="modal-panel__footer form-grid__full">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                取消
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting || uploading}>
                {submitting ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
